<template>
  <div class="min-h-screen">

    <portal to="mobile-header" v-slot="{didToggle, toggle}">
      <div class="flex-grow min-w-0 flex flex-col align-center justify-center h-12" @click="didToggle">
        <div class="align-center text-lg font-bold px-1 font-display inline-flex items-center justify-center sm:hidden">
          <div class="text-center truncate">
            Search
          </div>
        </div>
      </div>
    </portal>

    <header class="hidden sm:block py-10">
      <div class="md:flex md:items-center md:justify-between max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex-1 min-w-0">
          <h2 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:leading-9 sm:truncate">
            Search
          </h2>
        </div>
      </div>
    </header>

    <main class="">
      <div class="max-w-7xl mx-auto pb-12 px-4 sm:px-6 lg:px-8">
        <div class="bg-white rounded-lg shadow-lg border border-gray-200 px-5 py-6 sm:px-6">
          <div class="mt-8 sm:mt-0 mb-4 flex">
            <input aria-label="Email" v-model="proposedSearch" class="appearance-none block w-full px-3 py-3 border border-gray-300 text-base leading-6 rounded-md placeholder-gray-500 shadow-sm focus:outline-none focus:placeholder-gray-400 focus:ring-blue focus:border-blue-300 transition duration-150 ease-in-out sm:flex-1" placeholder="Student, Number, Group, or Grade Level">
          </div>

          <div class="relative grid gap-6 bg-white sm:gap-8">
            <div class="p-4 flex flex-col items-center justify-center my-24" v-if="search.length == 0">
              <div class="text-6xl mb-4">🕵️‍♂️</div>
              <div class="font-bold text-lg text-center">No results found</div>
              <div class="mt-1 text-sm text-center">Try another search term</div>
            </div>
            <div v-for="result in search" :key="result.__typename + result.id" class="first:mt-8">
              <router-link
                :to="{name: 'student', params: {studentId: result.id}}"
                v-if="result.__typename === 'Student'"
                class="-m-3 p-3 flex items-start space-x-4 rounded-lg hover:bg-gray-50 transition ease-in-out duration-150"
              >
                <svg class="flex-shrink-0 h-6 w-6 text-indigo-600" fill="currentColor" viewBox="0 0 24 24" stroke="currentColor">
                  <use href="/images/regular.svg#user-graduate"></use>
                </svg>
                <div class="space-y-1">
                  <p class="text-base leading-6 font-medium text-gray-900">
                    <StudentName :student="result" v-slot="{ display }">{{ display }}</StudentName>
                  </p>
                  <p class="text-sm leading-5 text-gray-500">
                    Student
                  </p>
                </div>
              </router-link>
              <router-link
              :to="{name: 'group', params: {groupId: result.id}}"
              v-if="result.__typename === 'Group'"
                class="-m-3 p-3 flex items-start space-x-4 rounded-lg hover:bg-gray-50 transition ease-in-out duration-150"
              >
                <svg class="flex-shrink-0 h-6 w-6 text-indigo-600" fill="currentColor" viewBox="0 0 24 24" stroke="currentColor">
                  <use href="/images/regular.svg#chalkboard-teacher"></use>
                </svg>
                <div class="space-y-1">
                  <p class="text-base leading-6 font-medium text-gray-900">
                    {{result.name}}
                  </p>
                  <p class="text-sm leading-5 text-gray-500">
                    Group
                  </p>
                </div>
              </router-link>
              <router-link
              :to="{name: 'gradeLevel', params: {gradeLevelId: result.id}}"
              v-if="result.__typename === 'GradeLevel'"
                class="-m-3 p-3 flex items-start space-x-4 rounded-lg hover:bg-gray-50 transition ease-in-out duration-150"
              >
                <svg class="flex-shrink-0 h-6 w-6 text-indigo-600" fill="currentColor" viewBox="0 0 24 24" stroke="currentColor">
                  <use href="/images/regular.svg#users-class"></use>
                </svg>
                <div class="space-y-1">
                  <p class="text-base leading-6 font-medium text-gray-900">
                    {{result.name}}
                  </p>
                  <p class="text-sm leading-5 text-gray-500">
                    Grade Level
                  </p>
                </div>
              </router-link>
              <router-link
              :to="{name: 'homeNumber', params: {homeNumberId: result.id}}"
              v-if="result.__typename === 'HomeNumber'"
                class="-m-3 p-3 flex items-start space-x-4 rounded-lg hover:bg-gray-50 transition ease-in-out duration-150"
              >
                <svg class="flex-shrink-0 h-6 w-6 text-indigo-600" fill="currentColor" viewBox="0 0 24 24" stroke="currentColor">
                  <use href="/images/regular.svg#phone-rotary"></use>
                </svg>
                <div class="space-y-1">
                  <p class="text-base leading-6 font-medium text-gray-900">
                    {{ result.label }} {{ result.formattedNumber || result.number }}
                  </p>
                  <p class="text-sm leading-5 text-gray-500">
                    Home Number
                  </p>
                </div>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import gql from 'graphql-tag'

import ButtonSubmit from '@/components/ButtonSubmit.vue'
import Panel        from '@/components/Panel.vue'
import StudentName  from '@/components/StudentName.vue'

export default {
  components: {
    ButtonSubmit,
    Panel,
    StudentName
  },

  props: {
    teamId: {
      required: true
    }
  },

  data () {
    return {
      proposedSearch: this.$route.query.query ? this.$route.query.query : '' ,
      loadingCount: 0,
      search: []
    }
  },

  computed: {
    noSearchEntered() {
      return this.proposedSearch === ''
    },

    isLoading() {
      return this.loadingCount > 0
    },

    hasNoItems() {
      return !this.isLoading && this.search.length === 0 && !this.noSearchEntered
    },

    hasItems() {
      return this.search.length > 0
    }
  },

  apollo: {
    search: {
      query: gql`query search($teamId: ID!, $search: String) {
        search(teamId: $teamId, search: $search) {
          ...on Student {
            id
            first
            last
          }
          ...on GradeLevel {
            id
            name
          }

          ...on HomeNumber {
            id
            label
            number
            formattedNumber
          }

          ...on Group {
            id
            name
          }
        }
      }`,
      variables() {
        return {
          teamId: this.teamId,
          search: this.proposedSearch
        }
      },

      skip() {
        return this.proposedSearch === '' || this.proposedSearch === undefined
      },
      loadingKey: 'loadingCount',
      debounce: 500,
      fetchPolicy: 'no-cache'
    }
  }

}
</script>
